import React, { ButtonHTMLAttributes } from "react";
import { motion, useReducedMotion } from "framer-motion";
import openSound from "../../../../assets/audio/menu-open.wav";
import { useAudioEffects } from "../../../../../../hooks/use-audio-effects";
export interface CardButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "onDrag" | "onDragStart" | "onDragEnd" | "onAnimationStart"
> {
  className?: string;
  children: React.ReactNode;
  colors?: {
    from?: string;
    via?: string;
    to?: string;
  };
}

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  // fallback to original blue glow if hex parsing fails (e.g. "transparent")
  if (isNaN(r) || isNaN(g) || isNaN(b)) return `rgba(120,170,255,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
}

const containerVariants: any = {
  initial: { opacity: 0, x: -20 },
  animate: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
  hover: {
    scale: 1.03,
    transition: { duration: 0.18, ease: "easeOut" },
  },
  tap: {
    scale: 0.962,
    transition: { duration: 0.08 },
  },
};

const diamondVariants: any = {
  initial: { opacity: 0, scale: 0, rotate: 50 },
  animate: {
    opacity: 1,
    scale: 1,
    rotate: 50,
    transition: { delay: 0.3, type: "spring", stiffness: 260, damping: 18 },
  },
  hover: {
    scale: [1, 1.6, 1],
    rotate: [50, 95, 50],
    transition: { duration: 0.65, ease: "easeInOut" },
  },
};

const glowVariants: any = {
  initial: { opacity: 0 },
  animate: { opacity: 0 },
  hover: { opacity: 1, transition: { duration: 0.25 } },
};

const shimmerVariants: any = {
  initial: { x: "-130%", opacity: 0 },
  animate: { x: "-130%", opacity: 0 },
  hover: {
    x: "230%",
    opacity: [0, 1, 0],
    transition: { duration: 0.6, ease: "easeInOut" },
  },
};

const textVariants: any = {
  initial: { x: 0 },
  animate: { x: 0 },
  hover: { x: 5, transition: { duration: 0.2, ease: "easeOut" } },
};

const CardButton: React.FC<CardButtonProps> = ({
  className = "",
  children,
  colors,
  ...props
}) => {
  const shouldReduceMotion = useReducedMotion();

  const from = colors?.from ?? "#3d4a5e";
  const via = colors?.via ?? "#2e3d56";
  const to = colors?.to ?? "transparent";

  const { playEffect } = useAudioEffects();

  return (
    // 3D rotateX/rotateY mouse-tracking removed — per-frame spring recalcs on
    // every mousemove pushed JS work every frame. Scale + shimmer sweep kept.
    <motion.button
      className={`p-[1px] rounded-md bg-gradient-to-r from-white/45 via-white/10 to-transparent relative ${className}`}
      variants={shouldReduceMotion ? undefined : containerVariants}
      initial="initial"
      animate="animate"
      whileHover="hover"
      whileTap="tap"
      {...props}
      onClick={(e) => {
        playEffect(openSound);
        if (props.onClick) props.onClick(e);
      }}
    >
      {/* 1. Hover glow ring */}
      <motion.span
        aria-hidden
        className="absolute inset-0 rounded-md pointer-events-none"
        style={{ boxShadow: `0 0 22px 5px ${hexToRgba(from, 0.6)}` }}
        variants={shouldReduceMotion ? undefined : glowVariants}
      />

      {/* 2. Diamond connector */}
      <motion.span
        aria-hidden
        className="w-[12px] h-[12px] bg-white shadow-2xl absolute top-[40%]  -left-[5px] z-[10]"
        variants={shouldReduceMotion ? undefined : diamondVariants}
      />

      {/* Inner card */}
      <div
        className="rounded-md w-full p-3 pl-4 font-semibold text-[0.9rem] text-left text-white min-w-[180px] [text-shadow:0_2px_4px_rgba(255,255,255,0.2)] overflow-hidden relative"
        style={{
          background: `linear-gradient(to right, ${from}, ${via}, ${to})`,
        }}
      >
        {/* 3. Shimmer sweep */}
        <motion.div
          aria-hidden
          className="absolute inset-y-0 w-[35%] bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-[15deg] pointer-events-none"
          variants={shouldReduceMotion ? undefined : shimmerVariants}
        />

        {/* 4. Text nudge */}
        <motion.span
          className="relative z-10 block"
          variants={shouldReduceMotion ? undefined : textVariants}
        >
          {children}
        </motion.span>
      </div>
    </motion.button>
  );
};

export default CardButton;
